import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_API_URL;

class SocketService {
  constructor() {
    this.socket = null;
    // Conversation rooms the open UI wants. Rooms live on the server-side
    // connection, so every (re)connect rejoins them.
    this.rooms = new Set();
  }

  connect() {
    if (this.socket) {
      // Reuse the one socket: a fresh io() would drop every listener the
      // open chat registered on this one.
      if (!this.socket.connected) this.socket.connect();
      return this.socket;
    }

    console.log('🔌 Creating NEW socket connection to:', SOCKET_URL);

    this.socket = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      // Keep retrying with backoff. A capped count left a tab that stayed
      // open across a server restart offline for good.
      reconnectionAttempts: Infinity,
      // The API authenticates the socket handshake with the same Firebase
      // ID token the REST calls send in `x-auth-token`. Passed as a
      // function so every reconnect re-reads storage and picks up a
      // refreshed token instead of replaying the one from first connect.
      auth: (cb) => {
        let token = null;
        try {
          token =
            localStorage.getItem('token') || sessionStorage.getItem('token');
        } catch (_) {
          // storage can be blocked — the server will refuse the socket and
          // the chat falls back to its REST fetches
        }
        cb({ token: token || '' });
      },
    });

    this.socket.on('connect', () => {
      console.log('✅ Connected to socket server, ID:', this.socket.id);
      // Without this an open thread silently stopped receiving messages
      // after any reconnect (or when it opened before the first connect).
      this.rooms.forEach((id) => this.socket.emit('join_conversation', id));
    });

    this.socket.on('disconnect', (reason) => {
      console.log(
        '❌ Disconnected from socket server, ID:',
        this.socket?.id,
        'Reason:',
        reason
      );
    });

    this.socket.on('connect_error', (error) => {
      console.error('🔥 Socket connection error:', error.message);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Reconnected after', attemptNumber, 'attempts');
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Reconnection attempt:', attemptNumber);
    });

    // Debug all socket events in development
    if (process.env.NODE_ENV === 'development') {
      this.socket.onAny((eventName, ...args) => {
        console.log(
          '🔍 SOCKET.JS: Received event:',
          eventName,
          'with data:',
          args
        );
      });
    }

    return this.socket;
  }

  disconnect() {
    console.log('🔴 DISCONNECT CALLED!');
    console.trace('Stack trace:');
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.rooms.clear();
  }

  isConnected() {
    return this.socket && this.socket.connected;
  }

  joinConversation(conversationId) {
    if (!conversationId) return;
    this.rooms.add(conversationId);
    if (this.socket && this.socket.connected) {
      console.log('🚪 SOCKET.JS: Joining conversation:', conversationId);
      this.socket.emit('join_conversation', conversationId);
    }
    // Not connected yet: the 'connect' handler joins it.
  }

  leaveConversation(conversationId) {
    this.rooms.delete(conversationId);
    if (this.socket && this.socket.connected) {
      console.log('🚪 SOCKET.JS: Leaving conversation:', conversationId);
      this.socket.emit('leave_conversation', conversationId);
    }
  }

  sendMessage(messageData) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('send_message', messageData);
    } else {
      console.error('❌ SOCKET.JS: Socket not connected!');
    }
  }

  onReceiveMessage(callback) {
    if (this.socket) {
      this.socket.on('receive_message', (message) => {
        callback(message);
      });
    } else {
      console.error(
        '❌ SOCKET.JS: Cannot setup listener - socket not available'
      );
    }
  }

  onMessageSent(callback) {
    if (this.socket) {
      this.socket.on('message_sent', (data) => {
        callback(data);
      });
    }
  }

  // Plain subscribe/unsubscribe, so a component can remove exactly the
  // handler it added (the on* helpers above wrap theirs and can't be removed).
  on(event, handler) {
    if (this.socket) this.socket.on(event, handler);
  }

  off(event, handler) {
    if (this.socket) this.socket.off(event, handler);
  }

  emitTyping(conversationId, userId, userName) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('typing', { conversationId, userId, userName });
    } else {
      console.error('❌ SOCKET.JS: Cannot emit typing - socket not connected');
    }
  }

  emitStopTyping(conversationId, userId) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('stop_typing', { conversationId, userId });
    } else {
      console.error(
        '❌ SOCKET.JS: Cannot emit stop typing - socket not connected'
      );
    }
  }

  onUserTyping(callback) {
    if (this.socket) {
      this.socket.on('user_typing', (data) => {
        callback(data);
      });
    } else {
      console.error(
        '❌ SOCKET.JS: Cannot setup user_typing listener - socket not available'
      );
    }
  }

  onUserStopTyping(callback) {
    if (this.socket) {
      this.socket.on('user_stop_typing', (data) => {
        callback(data);
      });
    } else {
      console.error(
        '❌ SOCKET.JS: Cannot setup user_stop_typing listener - socket not available'
      );
    }
  }

  onUnreadCountUpdated(callback) {
    if (this.socket) {
      this.socket.on('unread_count_updated', callback);
    }
  }

  removeAllListeners() {
    if (this.socket) {
      console.log('🧹 Removing all socket listeners');
      this.socket.removeAllListeners();
    }
  }

  // Fitbit WebSocket methods
  joinUserRoom(userId) {
    if (this.socket && this.socket.connected) {
      console.log('🚪 SOCKET.JS: Joining user room:', userId);
      this.socket.emit('join_user_room', userId);
    } else {
      console.error('❌ SOCKET.JS: Cannot join user room - socket not connected');
    }
  }

  onFitbitDataUpdated(callback) {
    if (this.socket) {
      this.socket.on('fitbit_data_updated', (data) => {
        console.log('📊 SOCKET.JS: Fitbit data updated:', data);
        callback(data);
      });
    } else {
      console.error(
        '❌ SOCKET.JS: Cannot setup fitbit_data_updated listener - socket not available'
      );
    }
  }

  removeFitbitListeners() {
    if (this.socket) {
      this.socket.off('fitbit_data_updated');
    }
  }
}

const socketService = new SocketService();
export default socketService;
